import { isOfType } from "../../utils";

import type {
  IAttributeTemplate,
  IDataObjectOrTemplate,
  IObjectTemplate,
  IRunObject,
  IScalarAttribute,
  ISpecObjectWithTemplate,
} from ".";

export interface ILinkByUid {
  type: "link_by_uid";
  id: string;
  scope: string;
}

export const isLinkUid = isOfType<ILinkByUid>("link_by_uid");

interface IColumnMappingPointer {
  column_name: string;
  index: number;
}

type FieldValueTypes =
  | "name"
  | "notes"
  | "description"
  | "label"
  | "tag"
  | "id"
  | "scope"
  | "filename"
  | "file_url"
  | "source.by"
  | "source.date"
  | "origin"
  | "value.nominal"
  | "value.mean"
  | "value.std"
  | "value.lower_bound"
  | "value.upper_bound"
  | "value.units"
  | "value.category"
  | "value.formula"
  | "value.smiles"
  | "value.inchi"
  | "absolute_quantity.units"
  | "absolute_quantity.nominal"
  | "absolute_quantity.mean"
  | "absolute_quantity.std"
  | "absolute_quantity.lower_bound"
  | "absolute_quantity.upper_bound"
  | "mass_fraction.nominal"
  | "mass_fraction.mean"
  | "mass_fraction.std"
  | "mass_fraction.lower_bound"
  | "mass_fraction.upper_bound"
  | "volume_fraction.nominal"
  | "volume_fraction.mean"
  | "volume_fraction.std"
  | "volume_fraction.lower_bound"
  | "volume_fraction.upper_bound"
  | "number_fraction.nominal"
  | "number_fraction.mean"
  | "number_fraction.std"
  | "number_fraction.lower_bound"
  | "number_fraction.upper_bound"
  | "material.id"
  | "material.scope"
  | "process.id"
  | "process.scope"
  | "template.id"
  | "template.scope"
  | "spec.id"
  | "spec.scope";

interface IField {
  value: FieldValueTypes;
}

interface BaseAttributeTemplatePointer {
  id: string;
  scope: string;
  // "Optional: If this is a pointer to a value type, the value type being identified"
  value_type:
    | "nominal_real"
    | "nominal_categorical"
    | "smiles"
    | "inchi"
    | "discrete_categorical"
    | "nominal_composition"
    | "nominal_integer"
    | "uniform_integer"
    | "uniform_real"
    | "normal_real"
    | "empirical_formula";
  // "Optional: If this is a pointer to a Real Value type, the units of that value.";
  units?: string;
}

export type UnitsAttributeTemplatePointer =
  | {
      value_type: "nominal_real";
      units: string;
    }
  | {
      units?: undefined;
    };

type AttributeTemplatePointer = BaseAttributeTemplatePointer &
  UnitsAttributeTemplatePointer;

export interface IFieldMappingPointer {
  attribute_template_pointer?: AttributeTemplatePointer;
  field: IField;
}

type ColumnMappingType = "column_mapping" | "user_entered";

interface IColumnMapping {
  type: ColumnMappingType;
  column_name_and_index: IColumnMappingPointer;
  object_pointer?: ILinkByUid;
  field_pointer?: IFieldMappingPointer;
}
const isColumnMapping = isOfType<IColumnMapping>(
  "column_mapping",
  "user_entered",
);

export interface IMappableField {
  mapping: IColumnMapping | null;
  value: string | null;
}

export const isMappableField = (arg: any): arg is IMappableField =>
  isColumnMapping(arg?.mapping) && typeof arg?.value === "string";

export interface Uids {
  [scope: string]: string;
  id: string;
}

interface HasUids {
  uids: Uids;
}
export function getDataObjectId<Type extends HasUids | ILinkByUid | undefined>(
  dataObject: Type,
): undefined extends Type
  ? Type extends undefined
    ? undefined
    : string
  : string {
  if (dataObject) {
    // this is a hack for a bug in TS 4.9
    // https://github.com/microsoft/TypeScript/pull/51502
    const anyObject: any = dataObject;
    if ("uids" in anyObject) {
      return anyObject.uids.id;
    }
    return anyObject.id;
  }
  return undefined as any;
}

/* 
Gets bounds for an attribute out of a material history context using the following logic:
  If there is an Attribute Template referenced in the Attribute:
    If there is an Object Template referenced in the object:
      Find the Object Template Attribute tuple that has the matching Attribute Template as its first element
      If the second tuple element is not null:
        Use that bounds to validate
      Else:
        Use the Attribute Template bounds
    Else:
      Use the Attribute Template bounds
  Else:
    No validation
*/
export function getBoundsForAttribute(
  attribute: IScalarAttribute,
  object: IRunObject,
  materialContext: Partial<Record<string, IDataObjectOrTemplate>>,
) {
  if (attribute.template) {
    const templateAttributeId = getDataObjectId(attribute.template);
    const attributeTemplate = materialContext[templateAttributeId] as
      IAttributeTemplate | undefined;

    if (attributeTemplate) {
      const objectSpecId = getDataObjectId(object.spec);
      const spec = materialContext[objectSpecId] as
        ISpecObjectWithTemplate | undefined;

      if (spec) {
        const specTemplateId = getDataObjectId(spec.template);

        if (specTemplateId) {
          const objectTemplate = materialContext[specTemplateId] as
            IObjectTemplate | undefined;

          if (objectTemplate) {
            const combinedAttributes = [
              ...(objectTemplate.conditions ?? []),
              ...(objectTemplate.parameters ?? []),
              ...(objectTemplate.properties ?? []),
            ];

            const templateAttributePair = combinedAttributes.find(
              ([templatedAttribute, bounds]) =>
                Boolean(bounds) &&
                getDataObjectId(templatedAttribute) ===
                  attributeTemplate.uids.id,
            );

            if (templateAttributePair) {
              return templateAttributePair[1];
            }
          }
        }
      }

      return attributeTemplate.bounds;
    }
  }
}
